const EMAIL_PROVIDER_LIST = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.ca',
  'yahoo.com.au',
  'hotmail.com',
  'hotmail.co.uk',
  'outlook.com',
  'outlook.co.uk',
  'live.com',
  'live.co.uk',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'zoho.com',
  'mail.com',
  'email.com',
  'fastmail.com',
  'fastmail.fm',
  'ymail.com',
  'rocketmail.com',
  'gmx.com',
  'gmx.net',
  'gmx.de',
  'web.de',
  't-online.de',
  'orange.fr',
  'wanadoo.fr',
  'free.fr',
  'libero.it',
  'virgilio.it',
  'qq.com',
  '163.com',
  '126.com',
  'sina.com',
  'naver.com',
  'daum.net',
  'hanmail.net',
  'rediffmail.com',
  'yandex.com',
  'yandex.ru',
  'mail.ru',
  'rambler.ru',
  'inbox.com',
  'hushmail.com',
  'tutanota.com',
  'hey.com',
  'pm.me'
] as const

const RESERVED_EXACT_HOSTS = ['localhost', 'example.com', 'example.org', 'example.net'] as const
const RESERVED_HOST_SUFFIXES = ['.local', '.test', '.invalid', '.example'] as const

export type WebsiteResearchIneligibilityReason = 'public_email_provider' | 'reserved_domain'

/**
 * Checks whether a hostname is one of the known public email providers.
 */
export function isEmailProvider(domain: string): boolean {
  return EMAIL_PROVIDER_LIST.indexOf(domain.trim().toLowerCase()) !== -1
}

/**
 * Extracts the domain from an email address.
 */
export function extractDomainFromEmail(email: string): string | null {
  if (!email || typeof email !== 'string') return null

  const trimmed = email.trim().toLowerCase()
  const atIndex = trimmed.lastIndexOf('@')

  if (atIndex === -1 || atIndex === trimmed.length - 1) return null

  const domain = trimmed.substring(atIndex + 1)

  if (domain.indexOf('.') === -1 || domain.length < 4) return null

  return domain
}

/**
 * Returns the reason a host or URL cannot be researched automatically.
 */
export function getWebsiteResearchIneligibilityReason(value: string): WebsiteResearchIneligibilityReason | null {
  const host = normalizeHostLikeValue(value)
  if (!host) return null

  if (isEmailProvider(host)) {
    return 'public_email_provider'
  }

  if (isReservedDomain(host)) {
    return 'reserved_domain'
  }

  return null
}

function normalizeHostLikeValue(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  const candidate = /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    const url = new URL(candidate)
    const normalized = url.hostname.toLowerCase().replace(/^www\./, '')
    return normalized || null
  } catch {
    return null
  }
}

function isReservedDomain(host: string): boolean {
  return isIpAddress(host) || RESERVED_EXACT_HOSTS.indexOf(host) !== -1 || RESERVED_HOST_SUFFIXES.some((suffix) => host.slice(-suffix.length) === suffix)
}

function isIpAddress(host: string): boolean {
  const normalized = host.replace(/^\[/, '').replace(/\]$/, '')

  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(normalized)) {
    return normalized.split('.').every((part) => {
      const value = Number(part)
      return !Number.isNaN(value) && String(value) === part && value >= 0 && value <= 255
    })
  }

  return normalized.indexOf(':') !== -1 && /^[\da-f:]+$/i.test(normalized)
}
